import React, { useState, useMemo, useEffect } from 'react';
import { 
  ShoppingCart, Star, ChevronRight, Sparkles, ShieldCheck, 
  Heart, Palette, Smile, Filter, Recycle, X, ArrowRight, Eye, 
  ShoppingBag, ArrowLeft, Plus, Minus, CreditCard 
} from 'lucide-react';

// Character Assets
import Toko from "../../Photos/Characters/TOKO.png"
import Omi from "../../Photos/Characters/Omi.png"
import Zips from "../../Photos/Characters/Zips.png"
import Bloop from "../../Photos/Characters/Bloop.png"
import Lumi from "../../Photos/Characters/Lumi.png"

const ShopPage = () => {
  // --- STATE MANAGEMENT ---
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [view, setView] = useState('gallery'); 
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);

  const heroCrew = [
    { img: Bloop, name: "Bloop", color: "text-orange-500", bg: "bg-orange-50" },
    { img: Toko, name: "Toko", color: "text-blue-500", bg: "bg-blue-50" },
    { img: Omi, name: "Omi", color: "text-pink-500", bg: "bg-pink-50" },
    { img: Zips, name: "Zips", color: "text-green-500", bg: "bg-green-50" },
    { img: Lumi, name: "Lumi", color: "text-purple-500", bg: "bg-purple-50" }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroCrew.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchPrintfulProducts = async () => {
      try {
        const workerUrl = process.env.REACT_APP_WORKER_URL;
        const response = await fetch(`${workerUrl}/store/products`);
        const data = await response.json();

        if (data.result) {
          const characterPool = [
            { mascot: Toko, theme: "#FFF9F0", accent: "orange" },
            { mascot: Omi, theme: "#F0F9FF", accent: "blue" },
            { mascot: Zips, theme: "#F0FDF4", accent: "green" },
            { mascot: Bloop, theme: "#F5F3FF", accent: "purple" },
            { mascot: Lumi, theme: "#FFF1F2", accent: "rose" }
          ];

          const liveProducts = data.result.map((p, index) => {
            const char = characterPool[index % characterPool.length];
            return {
              id: p.id,
              name: p.name,
              category: p.name.toLowerCase().includes('hat') || p.name.toLowerCase().includes('bag') 
                ? "Accessories" : "Apparel",
              price: 28,
              img: p.thumbnail_url,
              mascotImg: char.mascot,
              theme: char.theme,
              accentColor: char.accent,
              desc: "Crafted from 100% organic combed cotton. Features a tagless neckline for sensitive skin and reinforced 'double-stitch' seams for playground durability.",
              specs: {
                material: "100% Organic Cotton",
                fit: "Classic Unisex Kids Fit",
                care: "Machine wash cold, tumble dry low"
              }
            };
          });
          setProducts(liveProducts);
        }
      } catch (error) {
        console.error("Storefront Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPrintfulProducts();
  }, []);

  const addToCart = (product, size) => {
    setCart(prev => {
      const exists = prev.find(item => item.id === product.id && item.size === size);
      if (exists) {
        return prev.map(item => item.id === product.id && item.size === size 
          ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, { ...product, size, quantity: 1 }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (id, size) => {
    setCart(prev => prev.filter(item => !(item.id === id && item.size === size)));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const filteredProducts = useMemo(() => {
    return activeCategory === 'All' ? products : products.filter(p => p.category === activeCategory);
  }, [activeCategory, products]);

  const ProductCard = ({ product }) => (
    <div className="group cursor-pointer" onClick={() => { setSelectedProduct(product); setView('detail'); window.scrollTo(0,0); }}>
      <div className="relative aspect-[4/5] bg-slate-50 rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden mb-4 md:mb-6 transition-all duration-500 group-hover:shadow-2xl group-hover:-translate-y-2 border border-slate-100">
        <img src={product.img} alt={product.name} className="w-full h-full object-contain p-4 md:p-8 group-hover:scale-110 transition-transform duration-700" />
        <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="absolute bottom-4 md:bottom-6 left-4 right-4 md:left-6 md:right-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all hidden md:block">
          <button className="w-full bg-white/90 backdrop-blur-md text-slate-900 py-3 md:py-4 rounded-xl md:rounded-2xl font-black text-[9px] md:text-[10px] uppercase tracking-widest shadow-xl">
            View Details
          </button>
        </div>
      </div>
      <div className="px-1 flex justify-between items-start">
        <div className="max-w-[70%]">
          <p className="text-[8px] md:text-[9px] font-black text-orange-500 uppercase tracking-widest mb-1">{product.category}</p>
          <h3 className="text-sm md:text-lg font-black text-slate-900 leading-tight uppercase italic truncate md:whitespace-normal">{product.name}</h3>
        </div>
        <p className="font-bold text-slate-900 text-sm md:text-base">${product.price}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFFDF9] font-sans text-slate-800 overflow-x-hidden">
      
      {/* 1. TOP BAR */}
      <div className="bg-slate-900 text-white text-[8px] md:text-[10px] font-black uppercase tracking-[0.2em] md:tracking-[0.3em] py-2 md:py-3 text-center px-4">
        ⚡️ Shipping worldwide from the TalkyToon-iverse ⚡️
      </div>

      {/* 2. NAVIGATION */}
      <nav className="p-3 md:p-4 sticky top-0 z-[100] bg-[#FFFDF9]/80 backdrop-blur-xl border-b border-orange-100/30">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2 md:gap-3 cursor-pointer" onClick={() => setView('gallery')}>
            <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-500 rounded-lg md:rounded-xl flex items-center justify-center text-white shadow-lg">
              <Smile size={18} className="md:w-6 md:h-6" strokeWidth={2.5}/>
            </div>
            <span className="font-black text-lg md:text-2xl tracking-tighter">talkytoon<span className="text-orange-500">.</span></span>
          </div>
          
          <div className="hidden lg:flex gap-10 font-black text-[10px] uppercase tracking-[0.2em] text-slate-400">
            <button onClick={() => setView('gallery')} className={view === 'gallery' ? 'text-slate-900 border-b-2 border-orange-500 pb-1' : ''}>Gallery</button>
            <button className="hover:text-orange-500 transition-colors">Collections</button>
            <button className="hover:text-orange-500 transition-colors">About</button>
          </div>

          <button onClick={() => setIsCartOpen(true)} className="relative p-2 md:p-3 bg-slate-100 rounded-full hover:bg-orange-100 transition-colors">
            <ShoppingBag size={18} className="md:w-5 md:h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[9px] md:text-[10px] font-black w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center border-2 border-[#FFFDF9]">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </nav>

<main className="max-w-7xl mx-auto px-4 md:px-6 pt-1 pb-8 md:pb-12 lg:pb-20">
        {view === 'gallery' ? (
          <div className="space-y-16 md:space-y-32 animate-in fade-in duration-1000">
            
            {/* --- HERO SECTION --- */}
            <section className="relative rounded-[2rem] md:rounded-[4rem] bg-white border border-slate-100 min-h-[500px] md:min-h-[700px] overflow-hidden group">
              <div 
                className={`absolute inset-0 w-full h-full transition-all duration-1000 ease-in-out ${heroCrew[heroIndex].bg}`}
                style={{ 
                  clipPath: window.innerWidth > 1024 
                    ? 'polygon(0 0, 65% 0, 45% 100%, 0% 100%)' 
                    : 'polygon(0 0, 100% 0, 100% 45%, 0 55%)' 
                }}
              />

              <div className="relative z-10 grid lg:grid-cols-12 min-h-[500px] md:min-h-[700px]">
                <div className="lg:col-span-7 relative flex items-center justify-center p-6 md:p-12 order-2 lg:order-1">
                  <h2 className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[22vw] lg:text-[15vw] font-black text-white/40 uppercase italic tracking-tighter select-none whitespace-nowrap">
                    {heroCrew[heroIndex].name}
                  </h2>

                  <div className="relative w-full max-w-[240px] md:max-w-md lg:max-w-xl">
                    {heroCrew.map((char, i) => (
                      <img
                        key={char.name}
                        src={char.img}
                        alt={char.name}
                        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full transition-all duration-700 ease-out drop-shadow-[0_20px_30px_rgba(0,0,0,0.1)] md:drop-shadow-[0_50px_50px_rgba(0,0,0,0.1)] 
                          ${heroIndex === i ? 'opacity-100 scale-100 md:scale-110 rotate-0' : 'opacity-0 scale-50 rotate-12 pointer-events-none'}`}
                      />
                    ))}
                  </div>

                  <div className="absolute left-6 md:left-10 bottom-6 md:bottom-10 space-y-4 hidden sm:block">
                    <div className="flex items-center gap-2 md:gap-3 bg-white/80 backdrop-blur-md p-1.5 md:p-2 pr-4 md:pr-6 rounded-full border border-white/20 shadow-sm">
                      <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center ${heroCrew[heroIndex].bg}`}>
                        <ShieldCheck size={14} className={heroCrew[heroIndex].color} />
                      </div>
                      <span className="text-[7px] md:text-[9px] font-black uppercase tracking-widest text-slate-700">Non-Toxic Inks</span>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-5 flex items-center p-6 md:p-8 lg:p-20 order-1 lg:order-2">
                  <div className="space-y-4 md:space-y-8 w-full">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                         <Sparkles size={14} className={`${heroCrew[heroIndex].color} md:w-4 md:h-4`} />
                         <span className="text-[8px] md:text-[10px] font-black uppercase tracking-[0.2em] md:tracking-[0.3em] text-slate-400">Premium Kids Gear</span>
                      </div>
                      <h1 className="text-4xl md:text-6xl lg:text-8xl font-black text-slate-900 tracking-tighter leading-[0.9] uppercase italic">
                        Wear the <br />
                        <span className={`transition-colors duration-500 ${heroCrew[heroIndex].color}`}>Magic.</span>
                      </h1>
                    </div>

                    <p className="text-sm md:text-lg text-slate-500 font-medium leading-relaxed max-w-sm">
                      Designed for high-speed play and low-stress washing. Our <span className="text-slate-900 font-bold">Organic Cotton</span> line is made for kids who dream in full color.
                    </p>

                    <div className="space-y-6">
                      <button className="w-full lg:w-auto px-8 md:px-12 py-4 md:py-6 bg-slate-900 text-white rounded-2xl md:rounded-[2rem] font-black text-[10px] md:text-xs uppercase tracking-[0.2em] hover:bg-orange-600 transition-all shadow-xl flex items-center justify-center gap-4 group">
                        Shop {heroCrew[heroIndex].name} Gear
                        <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
                      </button>

                      <div className="pt-4 md:pt-8 border-t border-slate-100">
                        <p className="text-[8px] md:text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 mb-3 md:mb-4">Choose Your Hero</p>
                        <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 no-scrollbar">
                          {heroCrew.map((char, i) => (
                            <button 
                              key={i} 
                              onClick={() => setHeroIndex(i)}
                              className={`relative flex-shrink-0 w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl transition-all duration-300 border-2 overflow-hidden
                                ${heroIndex === i 
                                  ? 'border-slate-900 bg-white shadow-lg scale-110' 
                                  : 'border-slate-100 bg-slate-50 opacity-60'}`}
                            >
                              <img src={char.img} className="w-full h-full object-contain p-1.5 md:p-2" alt={char.name} />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* --- PRODUCT GRID --- */}
            <div className="space-y-8 md:space-y-12">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                <div>
                  <h3 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tighter uppercase italic leading-none">The Lineup</h3>
                  <p className="text-slate-400 font-bold text-[8px] md:text-[10px] uppercase tracking-widest mt-2">Filter by Character or Category</p>
                </div>
                <div className="flex w-full md:w-auto gap-2 bg-slate-100 p-1 rounded-xl md:rounded-2xl border border-slate-200">
                  {['All', 'Apparel', 'Accessories'].map(cat => (
                    <button 
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`flex-1 md:flex-none px-4 md:px-8 py-2 md:py-3 rounded-lg md:rounded-xl text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-all ${activeCategory === cat ? 'bg-white text-slate-900 shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 md:gap-x-10 gap-y-12 md:gap-y-24">
                {filteredProducts.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            </div>
          </div>
        ) : (
          /* --- DETAIL VIEW --- */
          <div className="animate-in fade-in slide-in-from-bottom-8 duration-700">
            <button onClick={() => setView('gallery')} className="group flex items-center gap-2 font-black text-[9px] md:text-[10px] uppercase tracking-[0.2em] md:tracking-[0.3em] text-slate-300 hover:text-orange-500 mb-8 md:mb-16 transition-all">
              <ArrowLeft size={16} className="group-hover:-translate-x-2 transition-transform"/> Back to Collection
            </button>

            <div className="grid lg:grid-cols-12 gap-10 md:gap-16 lg:gap-24 items-start">
              <div className="lg:col-span-7 space-y-6 md:space-y-8">
                <div className="relative group aspect-[4/5] bg-slate-50 rounded-[2rem] md:rounded-[4rem] flex items-center justify-center p-6 md:p-24 overflow-hidden border border-slate-100">
                  <img src={selectedProduct.img} alt={selectedProduct.name} className="relative z-10 w-full h-full object-contain drop-shadow-[0_20px_30px_rgba(0,0,0,0.1)] md:drop-shadow-[0_45px_45px_rgba(0,0,0,0.15)] transition-transform duration-700" />
                  <img src={selectedProduct.mascotImg} className="absolute bottom-6 right-6 w-16 md:w-32 opacity-20" alt="mascot" />
                </div>
                
                <div className="grid grid-cols-3 gap-3 md:gap-6">
                   <div className="aspect-square bg-blue-50 rounded-[1.5rem] md:rounded-[2.5rem] flex flex-col items-center justify-center p-3 md:p-6 text-center space-y-2 md:space-y-3 border border-blue-100">
                      <Smile className="text-blue-500 w-6 h-6 md:w-8 md:h-8" />
                      <p className="font-black text-[7px] md:text-[9px] uppercase tracking-widest text-blue-900 leading-tight">Adventure Ready</p>
                   </div>
                   <div className="aspect-square bg-orange-50 rounded-[1.5rem] md:rounded-[2.5rem] flex flex-col items-center justify-center p-3 md:p-6 text-center space-y-2 md:space-y-3 border border-orange-100">
                      <ShieldCheck className="text-orange-500 w-6 h-6 md:w-8 md:h-8" />
                      <p className="font-black text-[7px] md:text-[9px] uppercase tracking-widest text-orange-900 leading-tight">Safety First</p>
                   </div>
                   <div className="aspect-square bg-purple-50 rounded-[1.5rem] md:rounded-[2.5rem] flex flex-col items-center justify-center p-3 md:p-6 text-center space-y-2 md:space-y-3 border border-purple-100">
                      <Palette className="text-purple-500 w-6 h-6 md:w-8 md:h-8" />
                      <p className="font-black text-[7px] md:text-[9px] uppercase tracking-widest text-purple-900 leading-tight">Vibrant Arts</p>
                   </div>
                </div>
              </div>

              <div className="lg:col-span-5 space-y-8 md:space-y-12">
                <div className="space-y-4 md:space-y-6">
                  <div className="flex items-center justify-between">
                    <span className="text-orange-500 font-black text-[9px] md:text-[11px] uppercase tracking-[0.3em] md:tracking-[0.4em]">{selectedProduct.category}</span>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => <Star key={i} size={10} fill="#f59e0b" className="text-orange-400" />)}
                    </div>
                  </div>
                  
                  <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-slate-900 tracking-tighter uppercase italic leading-[0.85]">{selectedProduct.name}</h2>
                  
                  <div className="flex items-baseline gap-4">
                    <p className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight">${selectedProduct.price}.00</p>
                    <p className="text-slate-300 font-bold text-sm line-through">$42.00</p>
                  </div>

                  <p className="text-slate-500 text-sm md:text-lg font-medium leading-relaxed border-l-4 border-orange-100 pl-4 md:pl-6">
                    {selectedProduct.desc}
                  </p>
                </div>

                <div className="space-y-4 md:space-y-6">
                  <div className="flex justify-between items-end border-b border-slate-100 pb-3 md:pb-4">
                    <p className="font-black text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-slate-900">Select Age / Size</p>
                    <button className="text-[9px] md:text-[10px] font-black text-orange-500 uppercase underline-offset-4 md:underline-offset-8 underline transition-colors hover:text-orange-600">Size Chart</button>
                  </div>
                  <div className="grid grid-cols-4 gap-2 md:gap-4">
                    {['6M', '12M', '18M', '24M'].map(size => (
                      <button 
                        key={size}
                        onClick={() => setSelectedProduct({...selectedProduct, selectedSize: size})}
                        className={`py-3 md:py-5 rounded-xl md:rounded-3xl font-black text-xs md:text-sm transition-all border-2 ${selectedProduct.selectedSize === size ? 'border-slate-900 bg-slate-900 text-white shadow-xl -translate-y-1' : 'border-slate-100 text-slate-400 hover:border-slate-300'}`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4 pt-4 md:pt-6">
                  <button 
                    onClick={() => selectedProduct.selectedSize ? addToCart(selectedProduct, selectedProduct.selectedSize) : alert('Please select a size!')}
                    className="w-full py-5 md:py-8 bg-slate-900 text-white rounded-2xl md:rounded-[2.5rem] font-black text-[10px] md:text-[11px] uppercase tracking-[0.2em] md:tracking-[0.3em] hover:bg-orange-600 transition-all shadow-xl flex items-center justify-center gap-3 md:gap-4 group"
                  >
                    Add to Bag <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
                  </button>
                  <div className="grid grid-cols-2 gap-3 md:gap-4">
                     <button className="py-3 md:py-5 bg-white border-2 border-slate-100 rounded-xl md:rounded-[2rem] font-black text-[8px] md:text-[10px] uppercase tracking-widest text-slate-900 hover:bg-slate-50 transition-all">Save for Later</button>
                     <button className="py-3 md:py-5 bg-white border-2 border-slate-100 rounded-xl md:rounded-[2rem] font-black text-[8px] md:text-[10px] uppercase tracking-widest text-slate-900 hover:bg-slate-50 transition-all">Gift Registry</button>
                  </div>
                </div>

                <div className="pt-8 md:pt-12 border-t border-slate-100 space-y-6">
                  <div className="flex items-center gap-4 md:gap-5">
                    <div className="w-10 h-10 md:w-14 md:h-14 bg-green-50 rounded-xl md:rounded-2xl flex items-center justify-center text-green-600 shrink-0"><ShieldCheck size={20}/></div>
                    <div>
                      <h5 className="font-black text-[10px] md:text-[11px] uppercase tracking-widest">Safety Certified</h5>
                      <p className="text-[9px] md:text-[10px] text-slate-400 font-medium leading-tight">CPSC compliant, lead-free zippers, and phthalate-free prints.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 md:gap-5">
                    <div className="w-10 h-10 md:w-14 md:h-14 bg-orange-50 rounded-xl md:rounded-2xl flex items-center justify-center text-orange-600 shrink-0"><Recycle size={20}/></div>
                    <div>
                      <h5 className="font-black text-[10px] md:text-[11px] uppercase tracking-widest">Zero-Waste Printing</h5>
                      <p className="text-[9px] md:text-[10px] text-slate-400 font-medium leading-tight">Every item is made to order to reduce global textile waste.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* --- CART DRAWER --- */}
      <div className={`fixed inset-0 z-[200] ${isCartOpen ? 'visible' : 'invisible'}`}>
        <div className={`absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-500 ${isCartOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setIsCartOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl transition-transform duration-500 ease-out p-6 md:p-8 flex flex-col ${isCartOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex justify-between items-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-black tracking-tighter uppercase italic">Your Bag <span className="text-orange-500 font-normal">({cartCount})</span></h2>
            <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-slate-50 rounded-full transition"><X /></button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-6 md:space-y-8 pr-2 custom-scrollbar">
            {cart.length === 0 ? (
              <div className="text-center py-16 md:py-20 opacity-20">
                <ShoppingBag size={64} className="mx-auto mb-4 md:w-20 md:h-20" />
                <p className="font-black text-[10px] uppercase tracking-widest">Bag is currently empty</p>
              </div>
            ) : (
              cart.map((item, i) => (
                <div key={`${item.id}-${item.size}`} className="flex gap-4 md:gap-6 group">
                  <div className="w-20 h-20 md:w-24 md:h-24 bg-slate-50 rounded-xl md:rounded-[1.5rem] flex items-center justify-center p-3 md:p-4 shrink-0">
                    <img src={item.img} alt={item.name} className="w-full object-contain" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex justify-between items-start">
                      <h4 className="font-black text-xs md:text-sm uppercase tracking-tight leading-tight pr-4">{item.name}</h4>
                      <button onClick={() => removeFromCart(item.id, item.size)} className="text-slate-300 hover:text-red-500 transition shrink-0"><X size={14}/></button>
                    </div>
                    <div className="flex gap-3 text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-400">
                      <span>Size: <span className="text-orange-500">{item.size}</span></span>
                      <span>Qty: {item.quantity}</span>
                    </div>
                    <p className="font-black text-xs md:text-sm pt-2">${item.price}.00</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {cart.length > 0 && (
            <div className="pt-6 md:pt-8 border-t border-slate-100 space-y-4 md:space-y-6">
              <div className="space-y-1 md:space-y-2">
                <div className="flex justify-between text-slate-400 font-bold uppercase text-[9px] md:text-[10px] tracking-widest">
                  <span>Subtotal</span>
                  <span>${cartTotal}.00</span>
                </div>
                <div className="flex justify-between items-end pt-1 md:pt-2">
                  <p className="font-black text-slate-900 uppercase text-[10px] md:text-xs tracking-widest">Total</p>
                  <p className="text-3xl md:text-4xl font-black text-slate-900 tracking-tighter">${cartTotal}.00</p>
                </div>
              </div>
              <button className="w-full py-5 md:py-6 bg-slate-900 text-white rounded-2xl md:rounded-3xl font-black uppercase tracking-[0.2em] text-[10px] hover:bg-orange-600 transition-all shadow-xl flex items-center justify-center gap-3 group">
                Checkout Now <CreditCard size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 4. FOOTER */}
      <footer className="bg-white px-4 md:px-6 py-12 md:py-24 border-t border-slate-100 mt-10 md:mt-20">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-10 md:gap-16">
          <div className="space-y-4 md:space-y-6 max-w-xs">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-900 rounded-lg md:rounded-xl flex items-center justify-center text-white"><Smile size={20} /></div>
              <span className="font-black text-xl md:text-2xl tracking-tighter uppercase italic">talkytoon.</span>
            </div>
            <p className="text-slate-400 text-xs md:text-sm font-medium leading-relaxed">Made for the bravest explorers in the universe. Premium quality, toon-approved comfort.</p>
          </div>
          <div className="flex gap-12 md:gap-20">
            <div className="space-y-4 md:space-y-6">
              <p className="font-black text-[9px] md:text-[10px] uppercase tracking-widest text-slate-900">Shop</p>
              <ul className="space-y-2 md:space-y-4 text-[9px] md:text-[10px] font-bold uppercase tracking-widest text-slate-400">
                <li className="hover:text-orange-500 cursor-pointer transition-colors">Apparel</li>
                <li className="hover:text-orange-500 cursor-pointer transition-colors">Accessories</li>
                <li className="hover:text-orange-500 cursor-pointer transition-colors">Limited Drop</li>
              </ul>
            </div>
            <div className="space-y-4 md:space-y-6">
              <p className="font-black text-[9px] md:text-[10px] uppercase tracking-widest text-slate-900">Support</p>
              <ul className="space-y-2 md:space-y-4 text-[9px] md:text-[10px] font-bold uppercase tracking-widest text-slate-400">
                <li className="hover:text-orange-500 cursor-pointer transition-colors">Shipping</li>
                <li className="hover:text-orange-500 cursor-pointer transition-colors">Returns</li>
                <li className="hover:text-orange-500 cursor-pointer transition-colors">Sizing</li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ShopPage;